import { Button, Card, Form, Space, Typography, message } from 'antd';
import {
  AimOutlined,
  PlusOutlined,
  PartitionOutlined,
} from '@ant-design/icons';
import { useEffect, useMemo, useState } from 'react';
import {
  Background,
  Controls,
  MiniMap,
  type Connection,
  type EdgeMouseHandler,
  MarkerType,
  ReactFlow,
  type Edge,
  type Node,
  type ReactFlowInstance,
} from '@xyflow/react';
import { WorkflowTemplateStepCard } from './WorkflowTemplateStepCard';
import type {
  Option,
  WorkflowActionSchema,
  WorkflowDecisionSchema,
} from './WorkflowTemplateBuilder.types';
import '@xyflow/react/dist/style.css';

type WorkflowTemplateStepsSectionProps = {
  workflowActionSchemas: Record<string, WorkflowActionSchema>;
  workflowDecisionSchemas: Record<string, WorkflowDecisionSchema>;
  conditionOptions: Option[];
};

const DEFAULT_STEP = {
  type: 'action',
  action: { type: undefined, inputs: {} },
};

const getAutoLayoutPositions = (steps: Array<Record<string, any>>) => {
  const stepIdToIndex = new Map<string, number>();
  steps.forEach((step, index) => {
    const stepId = String(step?.id || '').trim();
    if (stepId) {
      stepIdToIndex.set(stepId, index);
    }
  });

  const memo: Record<number, number> = {};
  const visiting = new Set<number>();

  const depthFor = (index: number): number => {
    if (memo[index] !== undefined) return memo[index];
    if (visiting.has(index)) return 0;

    visiting.add(index);
    const dependsOn = Array.isArray(steps[index]?.depends_on)
      ? steps[index].depends_on
      : [];

    const parentDepths = dependsOn
      .map((depId: string) => stepIdToIndex.get(depId))
      .filter(
        (value: number | undefined): value is number => value !== undefined
      )
      .map((depIndex) => depthFor(depIndex));

    const depth = parentDepths.length ? Math.max(...parentDepths) + 1 : 0;
    visiting.delete(index);
    memo[index] = depth;
    return depth;
  };

  const byDepth = new Map<number, number[]>();
  steps.forEach((_, index) => {
    const depth = depthFor(index);
    const list = byDepth.get(depth) || [];
    list.push(index);
    byDepth.set(depth, list);
  });

  const nextPositions: Record<string, { x: number; y: number }> = {};
  Array.from(byDepth.entries())
    .sort((a, b) => a[0] - b[0])
    .forEach(([depth, indices]) => {
      indices.forEach((index, row) => {
        nextPositions[String(index)] = {
          x: depth * 340,
          y: row * 150,
        };
      });
    });

  return nextPositions;
};

export const WorkflowTemplateStepsSection = ({
  workflowActionSchemas,
  workflowDecisionSchemas,
  conditionOptions,
}: WorkflowTemplateStepsSectionProps) => {
  const form = Form.useFormInstance();
  const [selectedStepIndex, setSelectedStepIndex] = useState<number | null>(
    null
  );
  const [nodePositions, setNodePositions] = useState<
    Record<string, { x: number; y: number }>
  >({});
  const [flowInstance, setFlowInstance] = useState<ReactFlowInstance | null>(
    null
  );
  const watchedSteps =
    (Form.useWatch(['workflow', 'steps'], form) as Array<
      Record<string, any>
    >) || [];

  const workflowActionOptions = useMemo(
    () =>
      Object.keys(workflowActionSchemas)
        .sort((a, b) => a.localeCompare(b))
        .map((actionType) => ({ label: actionType, value: actionType })),
    [workflowActionSchemas]
  );

  const workflowDecisionOptions = useMemo(
    () =>
      Object.keys(workflowDecisionSchemas)
        .sort((a, b) => a.localeCompare(b))
        .map((decisionType) => ({ label: decisionType, value: decisionType })),
    [workflowDecisionSchemas]
  );

  useEffect(() => {
    if (!watchedSteps.length) {
      setSelectedStepIndex(null);
      return;
    }

    if (
      selectedStepIndex === null ||
      selectedStepIndex < 0 ||
      selectedStepIndex >= watchedSteps.length
    ) {
      setSelectedStepIndex(0);
    }
  }, [watchedSteps.length, selectedStepIndex]);

  useEffect(() => {
    setNodePositions((prev) => {
      const autoPositions = getAutoLayoutPositions(watchedSteps);
      const next = { ...prev };
      const validNodeIds = new Set(
        watchedSteps.map((_, index) => String(index))
      );

      Object.keys(next).forEach((key) => {
        if (!validNodeIds.has(key)) {
          delete next[key];
        }
      });

      watchedSteps.forEach((_, index) => {
        const nodeId = String(index);
        if (!next[nodeId] && autoPositions[nodeId]) {
          next[nodeId] = autoPositions[nodeId];
        }
      });

      return next;
    });
  }, [watchedSteps]);

  return (
    <Card size="small" title="Steps">
      <Form.List name={['workflow', 'steps']}>
        {(fields, { add, remove }) => {
          const allSteps =
            (form.getFieldValue(['workflow', 'steps']) as Array<{
              id?: string;
              type?: string;
              depends_on?: string[];
            }>) || [];

          const stepOptions: Option[] = allSteps
            .map((step) => step?.id)
            .filter((stepId): stepId is string => Boolean(stepId))
            .map((stepId) => ({ label: stepId, value: stepId }));

          const stepIdToNodeId = new Map<string, string>();
          fields.forEach((_, index) => {
            const stepId = String(allSteps[index]?.id || '').trim();
            if (stepId) {
              stepIdToNodeId.set(stepId, String(index));
            }
          });

          const nodes: Node[] = fields.map((_, index) => {
            const stepId = String(allSteps[index]?.id || '').trim();
            const stepType = String(allSteps[index]?.type || 'action').trim();
            const title = stepId || `Step ${index + 1}`;
            const selected = index === selectedStepIndex;
            const isDecision = stepType === 'decision';

            const baseBackground = isDecision ? '#fff7e6' : '#e6f4ff';
            const baseBorder = isDecision ? '#faad14' : '#1677ff';
            const selectedBackground = isDecision ? '#ffe7ba' : '#bae0ff';
            const selectedBorder = isDecision ? '#d48806' : '#0958d9';

            return {
              id: String(index),
              position: nodePositions[String(index)] || { x: 0, y: 0 },
              data: {
                label: title,
              },
              style: {
                border: selected
                  ? `2px solid ${selectedBorder}`
                  : `1px solid ${baseBorder}`,
                borderRadius: isDecision ? 999 : 8,
                padding: 8,
                fontSize: 9,
                fontWeight: 600,
                minWidth: 180,
                textAlign: 'center',
                color: '#1f1f1f',
                background: selected ? selectedBackground : baseBackground,
                boxShadow: isDecision
                  ? 'inset 0 0 0 1px rgba(0,0,0,0.04)'
                  : 'none',
              },
            };
          });

          const edgeMap = new Map<string, Edge>();
          fields.forEach((_, index) => {
            const currentNodeId = String(index);
            const step = (allSteps[index] || {}) as Record<string, any>;

            const dependsOn = Array.isArray(step.depends_on)
              ? step.depends_on
              : [];
            dependsOn.forEach((depId: string) => {
              const source = stepIdToNodeId.get(depId);
              if (!source) return;
              const id = `dep-${source}-${currentNodeId}`;
              edgeMap.set(id, {
                id,
                source,
                target: currentNodeId,
                style: { strokeDasharray: '4 2' },
                label: 'depends',
              });
            });

            const defaultNext = String(step?.next?.default || '').trim();
            if (defaultNext && defaultNext !== 'end') {
              const target = stepIdToNodeId.get(defaultNext);
              if (target) {
                const id = `next-default-${currentNodeId}-${target}`;
                edgeMap.set(id, {
                  id,
                  source: currentNodeId,
                  target,
                  label: 'default',
                  markerEnd: { type: MarkerType.ArrowClosed },
                });
              }
            }

            const conditions = Array.isArray(step?.next?.conditions)
              ? step.next.conditions
              : [];
            conditions.forEach((condition: any, conditionIndex: number) => {
              const thenStep = String(condition?.then || '').trim();
              if (!thenStep || thenStep === 'end') return;
              const target = stepIdToNodeId.get(thenStep);
              if (!target) return;
              const id = `next-cond-${currentNodeId}-${target}-${conditionIndex}`;
              edgeMap.set(id, {
                id,
                source: currentNodeId,
                target,
                label: `if #${conditionIndex + 1}`,
                markerEnd: { type: MarkerType.ArrowClosed },
              });
            });
          });

          const edges = Array.from(edgeMap.values());
          const selectedIndex =
            selectedStepIndex !== null &&
            selectedStepIndex >= 0 &&
            selectedStepIndex < fields.length
              ? selectedStepIndex
              : 0;
          const selectedField = fields[selectedIndex];

          const updateSteps = (nextSteps: Array<Record<string, any>>) => {
            form.setFieldValue(['workflow', 'steps'], nextSteps);
          };

          const onConnect = (connection: Connection) => {
            if (!connection.source || !connection.target) return;
            const sourceIndex = Number(connection.source);
            const targetIndex = Number(connection.target);
            const sourceStep = allSteps[sourceIndex];
            const targetStep = allSteps[targetIndex];

            const targetStepId = String(targetStep?.id || '').trim();
            if (!targetStepId) {
              message.warning('Set target step ID before connecting nodes.');
              return;
            }
            if (!sourceStep) return;

            const nextSteps = JSON.parse(JSON.stringify(allSteps));
            const sourceNext = nextSteps[sourceIndex].next || {};
            sourceNext.default = targetStepId;
            nextSteps[sourceIndex].next = sourceNext;
            updateSteps(nextSteps);
            setSelectedStepIndex(sourceIndex);
          };

          const onEdgeClick: EdgeMouseHandler = (_, edge) => {
            const nextSteps = JSON.parse(JSON.stringify(allSteps));
            const parts = String(edge.id).split('-');
            const sourceIndex = Number(parts[1]);
            const targetIndex = Number(parts[2]);
            const sourceStepId = String(
              nextSteps[sourceIndex]?.id || ''
            ).trim();

            if (edge.id.startsWith('dep-')) {
              const dependsOn = Array.isArray(
                nextSteps[targetIndex]?.depends_on
              )
                ? nextSteps[targetIndex].depends_on
                : [];
              nextSteps[targetIndex].depends_on = dependsOn.filter(
                (depId: string) => depId !== sourceStepId
              );
              updateSteps(nextSteps);
              return;
            }

            if (edge.id.startsWith('next-default-')) {
              if (nextSteps[sourceIndex]?.next) {
                nextSteps[sourceIndex].next.default = undefined;
              }
              updateSteps(nextSteps);
              return;
            }

            if (edge.id.startsWith('next-cond-')) {
              const conditionIndex = Number(parts[4]);
              const conditions = Array.isArray(
                nextSteps[sourceIndex]?.next?.conditions
              )
                ? nextSteps[sourceIndex].next.conditions
                : [];
              if (conditionIndex >= 0 && conditionIndex < conditions.length) {
                conditions.splice(conditionIndex, 1);
                nextSteps[sourceIndex].next.conditions = conditions;
                updateSteps(nextSteps);
              }
            }
          };

          const handleAutoLayout = () => {
            setNodePositions(getAutoLayoutPositions(allSteps));
            requestAnimationFrame(() =>
              flowInstance?.fitView({ padding: 0.2 })
            );
          };

          return (
            <div className="space-y-4">
              {fields.length ? (
                <div className="grid grid-cols-1 xl:grid-cols-12 gap-4">
                  <Card
                    size="small"
                    title="Workflow Graph"
                    className="xl:col-span-9"
                    extra={
                      <Space size={8}>
                        <Button
                          size="small"
                          icon={<PartitionOutlined />}
                          onClick={handleAutoLayout}
                        >
                          Auto Layout
                        </Button>
                        <Button
                          size="small"
                          icon={<AimOutlined />}
                          onClick={() =>
                            flowInstance?.fitView({ padding: 0.2 })
                          }
                        >
                          Fit View
                        </Button>
                      </Space>
                    }
                  >
                    <div style={{ height: 560 }} className="border rounded-md">
                      <ReactFlow
                        fitView
                        nodes={nodes}
                        edges={edges}
                        onInit={setFlowInstance}
                        onConnect={onConnect}
                        onEdgeClick={onEdgeClick}
                        onNodeDragStop={(_, node) => {
                          setNodePositions((prev) => ({
                            ...prev,
                            [node.id]: node.position,
                          }));
                        }}
                        onNodeClick={(_, node) =>
                          setSelectedStepIndex(Number(node.id))
                        }
                      >
                        <Background />
                        <Controls />
                        <MiniMap pannable zoomable />
                      </ReactFlow>
                    </div>
                    <Typography.Text
                      type="secondary"
                      className="mt-2 block text-xs"
                    >
                      Tip: drag from one node to another to set default route.
                      Click an edge to remove that connection.
                    </Typography.Text>
                  </Card>
                  {selectedField ? (
                    <div className="xl:col-span-3 xl:sticky xl:top-4 xl:self-start">
                      <WorkflowTemplateStepCard
                        key={selectedField.key}
                        field={selectedField}
                        index={selectedIndex >= 0 ? selectedIndex : 0}
                        remove={remove}
                        stepOptions={stepOptions}
                        workflowActionOptions={workflowActionOptions}
                        workflowDecisionOptions={workflowDecisionOptions}
                        workflowActionSchemas={workflowActionSchemas}
                        workflowDecisionSchemas={workflowDecisionSchemas}
                        conditionOptions={conditionOptions}
                      />
                    </div>
                  ) : null}
                </div>
              ) : (
                <Typography.Text type="secondary">
                  Add at least one step to build the workflow template.
                </Typography.Text>
              )}

              <Button
                type="dashed"
                icon={<PlusOutlined />}
                onClick={() => {
                  add(DEFAULT_STEP);
                  setSelectedStepIndex(fields.length);
                }}
              >
                Add Step
              </Button>
            </div>
          );
        }}
      </Form.List>
    </Card>
  );
};
